<script setup lang="ts">
import type { TabKey } from '@/components/base/BaseTab.vue'
import type { DeliveryConnection } from '@/types/deliveryReviews'
import { DELIVERY_PLATFORMS } from '@/types/deliveryReviews'
import { fetchConnections } from './deliveryReviewsApi'
import DeliveryConnectionCard from './components/DeliveryConnectionCard.vue'
import DeliveryReviewList from './components/DeliveryReviewList.vue'

type TabValue = 'reviews' | 'connection'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { user, stores } = useLocalStorage()
const notification = useNotification()

const merchantId = computed<number>(() => user.value.merchant.id)
const connections = ref<DeliveryConnection[]>([])
const isLoaded = ref(false)

const hasConnectedStore = computed(() =>
  connections.value.some((connection) => connection.stores.some((store) => store.storeId !== null))
)

const currentTab = ref<TabValue>(route.query.tab === 'connection' ? 'connection' : 'reviews')
watch(currentTab, (tab) => router.replace({ query: { ...route.query, tab } }))

const tabs = computed<TabKey[]>(() => [
  { value: 'reviews', name: t('pages.deliveryReviews.tabs.reviews') },
  { value: 'connection', name: t('pages.deliveryReviews.tabs.connection') }
])

const orderedConnections = computed(() =>
  DELIVERY_PLATFORMS.map((platform) => connections.value.find((connection) => connection.platform === platform)).filter(
    (connection): connection is DeliveryConnection => connection !== undefined
  )
)

function onConnectionUpdated(updated: DeliveryConnection) {
  connections.value = connections.value.map((connection) =>
    connection.platform === updated.platform ? updated : connection
  )
}

onMounted(async () => {
  try {
    connections.value = await fetchConnections(merchantId.value)
    if (!route.query.tab && !hasConnectedStore.value) currentTab.value = 'connection'
  } catch {
    notification.create('error', { text: t('pages.deliveryReviews.loadError') })
  } finally {
    isLoaded.value = true
  }
})
</script>

<template>
  <div>
    <TheListHeader :title="t('pages.deliveryReviews.title')" />

    <div class="px-6 pb-6 sm:px-12 sm:pb-12">
      <p class="mb-4 max-w-3xl text-sm text-muted-foreground">{{ t('pages.deliveryReviews.description') }}</p>
      <BaseTab v-model="currentTab" :tabs="tabs" class="mb-4" />

      <AppSkeleton v-if="!isLoaded" class="h-48 w-full" />
      <template v-else-if="currentTab === 'reviews'">
        <DeliveryReviewList
          v-if="hasConnectedStore"
          :merchant-id="merchantId"
          :merchant-stores="stores"
          :connections="connections"
        />
        <Card v-else class="p-8 text-center">
          <p class="font-medium">{{ t('pages.deliveryReviews.reviews.notConnected.title') }}</p>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ t('pages.deliveryReviews.reviews.notConnected.description') }}
          </p>
          <Button variant="default-primary" class="mx-auto mt-4" @click="currentTab = 'connection'">
            {{ t('pages.deliveryReviews.reviews.notConnected.cta') }}
          </Button>
        </Card>
      </template>
      <div v-else class="grid gap-4 xl:grid-cols-2">
        <DeliveryConnectionCard
          v-for="connection in orderedConnections"
          :key="connection.platform"
          :merchant-id="merchantId"
          :connection="connection"
          :merchant-stores="stores"
          @updated="onConnectionUpdated"
        />
      </div>
    </div>
  </div>
</template>
